// ATL-001 (design doc 2026-06-11 § The three row states) — treasurer-facing
// copy for the `resolvedAs` engineer enum. Mirrors the `preflightCopy.ts`
// pattern: engineer enums never render verbatim
// (`feedback_treasurer_first_copy.md`); they translate to plain words at the
// FE render boundary. The Done-row check renders `resolvedAsLabel(item.resolvedAs)`.
//
// All five labels are defined up-front in ATL-001; ATL-002/003/004 CONSUME
// this map (they do not add their own words). The `Record<TriageResolvedAs,
// string>` type makes the map exhaustive — adding a `resolvedAs` value without
// a label is a compile error. Exhaustivity + the exact strings are pinned by
// `packages/shared/__tests__/sortInboxCopy.test.ts`.

import type { TriageResolvedAs } from '../generated/enums.gen';

/**
 * The word rendered next to the green check on a Done ledger row. Keyed by the
 * `resolvedAs` wire enum. Named PascalCase to match the sibling `PreflightCopy`
 * treasurer-copy map.
 */
export const ResolvedAsCopy: Record<TriageResolvedAs, string> = {
  Confirmed: 'Confirmed',
  Linked: 'Linked to original',
  Standalone: 'Standalone refund',
  // CAP-009 — treasurer label for the DepositOfRecordedChecks classifier
  // (design doc § Bank reconciliation posture / § Interactions with the
  // triage-ledger plan). The deposit line never becomes Reported; this is the
  // terminal Done word.
  DepositOfRecordedChecks: 'Deposit of recorded checks',
  // ADR-014 / EFR-001 — treasurer label for the single non-reporting resolution.
  // Names the consequence ("Not reported"), not the mechanism — the collapse
  // dropped the transfer-vs-personal distinction (only the optional note
  // survives). Shared "Not reported" lens word with voided money rows.
  Excluded: 'Not reported',
  // ADR-016 — a treasurer-voided money row. Same treasurer word as the bank-side
  // 'Excluded' ("Not reported"); the two stay distinct enum values for forensic
  // clarity but render identically (both off the report).
  Voided: 'Not reported',
};

/**
 * Treasurer copy for a `resolvedAs` value, or `null` when the row carries no
 * resolution word (NeedsAction / Reported rows pass `null`).
 */
export function resolvedAsLabel(
  resolvedAs: TriageResolvedAs | null,
): string | null {
  return resolvedAs === null ? null : ResolvedAsCopy[resolvedAs];
}
